import { Button } from "@/components/ui/UIElements/Button";
import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Icon, Switch } from "../";
import ExtraOptions from "./ExtraOptions";

const ExtrasList = ({
  businessId,
  hasPosIntegration,
  extras,
  isLoadingExtras,
  selectedProduct,
  onToggleExtra,
  isLoadingToggle,
  onCreateExtra,
  onEditExtra,
  onDeleteExtra,
  isDeletingExtra,
}) => {
  const { t } = useTranslation("products");
  const [searchTerm, setSearchTerm] = useState("");
  const searchInputRef = useRef(null);
  const [expanded, setExpanded] = useState({});
  const [isActiveExtrasExpanded, setIsActiveExtrasExpanded] = useState(true);
  const [isAvailableExtrasExpanded, setIsAvailableExtrasExpanded] =
    useState(false);

  if (!selectedProduct) return null;

  const toggleExpanded = (id) =>
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));

  const clearSearchAndRestoreFocus = () => {
    setSearchTerm("");
    searchInputRef.current?.focus();
  };

  const productExtras = selectedProduct.extras ?? [];
  const productExtraIds = new Set(
    productExtras.map((e) => e?.id).filter(Boolean),
  );

  const availableExtras = extras.filter(
    (extra) => !productExtraIds.has(extra.id),
  );

  // `selectedProduct.extras` can contain null/undefined entries; filter defensively before sorting.
  const sortedProductExtras = productExtras
    .filter(Boolean)
    .sort((a, b) => (a.rank || 0) - (b.rank || 0));
  const sortedAvailableExtras = [...availableExtras].sort(
    (a, b) => (a.rank || 0) - (b.rank || 0),
  );

  const lowerSearch = searchTerm.toLowerCase();
  const filteredProductExtras = searchTerm
    ? sortedProductExtras.filter((e) =>
        e.name?.toLowerCase().includes(lowerSearch),
      )
    : sortedProductExtras;
  const filteredAvailableExtras = searchTerm
    ? sortedAvailableExtras.filter((e) =>
        e.name?.toLowerCase().includes(lowerSearch),
      )
    : sortedAvailableExtras;

  const hasProductExtras = filteredProductExtras.length > 0;
  const hasAvailableExtras = filteredAvailableExtras.length > 0;
  const hasAnyExtras =
    hasProductExtras ||
    hasAvailableExtras ||
    extras.length > 0 ||
    sortedProductExtras.length > 0;

  const renderExtraItem = (extra, isEnabled) => {
    return (
      <div key={extra.id} className="border-border bg-card rounded-lg border">
        <div className="flex flex-wrap items-center justify-between gap-2 p-3">
          <Button
            variant="ghost"
            type="button"
            onClick={() => toggleExpanded(extra.id)}
            className="hover:bg-gray-750/20 flex w-full min-w-0 flex-auto items-center gap-2 rounded text-left sm:w-auto sm:flex-1"
            aria-label="Toggle options"
          >
            <Icon
              name={expanded[extra.id] ? "ChevronDown" : "ChevronRight"}
              className="text-muted-foreground h-4 w-4 flex-shrink-0"
            />
            <div className="min-w-0 flex-1">
              <h4 className="text-foreground text-sm font-medium whitespace-normal">
                {extra.name}
              </h4>
              {extra.description && (
                <p className="text-muted-foreground mt-1 text-xs whitespace-normal">
                  {extra.description}
                </p>
              )}
            </div>
          </Button>
          <div
            className="flex items-center gap-2"
            onClick={(e) => e.stopPropagation()}
          >
            {!hasPosIntegration && (
              <>
                <Button
                  variant="ghost"
                  type="button"
                  onClick={() => onEditExtra(extra)}
                  className="text-muted-foreground hover:bg-secondary hover:text-primary rounded-md p-1.5"
                  aria-label="Edit extra"
                  title="Edit extra"
                >
                  <Icon name="Edit" className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  type="button"
                  onClick={() => onDeleteExtra(extra)}
                  disabled={isDeletingExtra}
                  className="text-muted-foreground hover:bg-secondary hover:text-destructive rounded-md p-1.5 disabled:cursor-not-allowed disabled:opacity-50"
                  aria-label="Delete extra"
                  title="Delete extra"
                >
                  <Icon name="Trash" className="h-4 w-4" />
                </Button>
              </>
            )}
            <Switch
              aria-label={extra.name}
              checked={!!isEnabled}
              onCheckedChange={() =>
                onToggleExtra(extra, selectedProduct.extras)
              }
              disabled={isLoadingToggle}
            />
          </div>
        </div>

        {expanded[extra.id] && (
          <div className="border-border border-t">
            <ExtraOptions
              businessId={businessId}
              extraId={extra.id}
              hasPosIntegration={hasPosIntegration}
            />
          </div>
        )}
      </div>
    );
  };

  return (
    <div
      className="ordering-catalog-surface space-y-4"
      data-ordering-expression="product"
    >
      <div className="flex items-center justify-between">
        <div className="text-foreground text-sm">Extras</div>
        {!hasPosIntegration && (
          <div className="sm:ml-auto">
            <Button
              variant="default"
              type="button"
              className="flex items-center gap-1 px-3 py-2 disabled:cursor-not-allowed disabled:opacity-50"
              onClick={onCreateExtra}
            >
              <Icon name="Plus" className="h-4 w-4" /> Add Extra
            </Button>
          </div>
        )}
      </div>

      {/* Search Input */}
      <div className="relative">
        <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
          <Icon name="Search" className="text-muted-foreground h-4 w-4" />
        </div>
        <input
          ref={searchInputRef}
          type="text"
          aria-label={t("business.extras.searchPlaceholder")}
          placeholder={t("business.extras.searchPlaceholder")}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="border-border bg-card text-foreground focus:ring-ring w-full rounded-lg border py-2 pr-10 pl-10 text-sm placeholder-gray-400 focus:ring-2 focus:outline-none"
        />
        {searchTerm && (
          <Button
            variant="ghost"
            type="button"
            onClick={clearSearchAndRestoreFocus}
            aria-label={t("stores:search.clearSearch")}
            className="absolute inset-y-0 right-0 flex items-center pr-3"
          >
            <Icon
              name="X"
              className="text-muted-foreground h-4 w-4 hover:text-white"
            />
          </Button>
        )}
      </div>

      {isLoadingExtras ? (
        <div className="flex items-center justify-center py-8">
          <div className="border-primary h-8 w-8 animate-spin rounded-full border-b-2"></div>
        </div>
      ) : hasAnyExtras ? (
        <>
          {/* Active Extras Section */}
          {hasProductExtras && (
            <div className="space-y-3">
              <Button
                variant="ghost"
                type="button"
                onClick={() =>
                  setIsActiveExtrasExpanded(!isActiveExtrasExpanded)
                }
                className="group hover:bg-card/50 flex w-full items-center justify-between gap-2 rounded-lg px-3 py-2 transition-colors"
                aria-label={
                  isActiveExtrasExpanded
                    ? "Collapse active extras"
                    : "Expand active extras"
                }
              >
                <div className="flex items-center gap-2">
                  <Icon
                    name={
                      isActiveExtrasExpanded ? "ChevronDown" : "ChevronRight"
                    }
                    className="text-muted-foreground group-hover:text-foreground h-4 w-4 flex-shrink-0 transition-transform"
                  />
                  <h3 className="text-foreground text-sm font-semibold">
                    Active Extras
                  </h3>
                  <span className="bg-secondary text-muted-foreground rounded px-2 py-0.5 text-xs">
                    {filteredProductExtras.length}
                  </span>
                </div>
              </Button>
              {isActiveExtrasExpanded && (
                <div className="space-y-2 pl-7">
                  {filteredProductExtras.map((extra) =>
                    renderExtraItem(extra, true),
                  )}
                </div>
              )}
            </div>
          )}

          {/* Available Extras Section */}
          {hasAvailableExtras && (
            <div className="space-y-3">
              {hasProductExtras && (
                <div className="border-border border-t pt-4"></div>
              )}
              <Button
                variant="ghost"
                type="button"
                onClick={() =>
                  setIsAvailableExtrasExpanded(!isAvailableExtrasExpanded)
                }
                className="group hover:bg-card/50 flex w-full items-center justify-between gap-2 rounded-lg px-3 py-2 transition-colors"
                aria-label={
                  isAvailableExtrasExpanded
                    ? "Collapse available extras"
                    : "Expand available extras"
                }
              >
                <div className="flex items-center gap-2">
                  <Icon
                    name={
                      isAvailableExtrasExpanded ? "ChevronDown" : "ChevronRight"
                    }
                    className="text-muted-foreground group-hover:text-foreground h-4 w-4 flex-shrink-0 transition-transform"
                  />
                  <h3 className="text-foreground text-sm font-semibold">
                    Available Extras
                  </h3>
                  <span className="bg-secondary text-muted-foreground rounded px-2 py-0.5 text-xs">
                    {filteredAvailableExtras.length}
                  </span>
                </div>
              </Button>
              {isAvailableExtrasExpanded && (
                <div className="space-y-2 pl-7">
                  {filteredAvailableExtras.map((extra) =>
                    renderExtraItem(extra, false),
                  )}
                </div>
              )}
            </div>
          )}

          {/* No search results */}
          {searchTerm && !hasProductExtras && !hasAvailableExtras && (
            <div className="py-8 text-center">
              <Icon
                name="Search"
                className="text-muted-foreground mx-auto mb-2 h-12 w-12"
              />
              <p className="text-muted-foreground text-sm">
                {t("business.extras.noSearchResults")}
              </p>
            </div>
          )}
        </>
      ) : (
        <div className="py-8 text-center">
          <Icon
            name="Tag"
            className="text-muted-foreground mx-auto mb-2 h-12 w-12"
          />
          <p className="text-muted-foreground text-sm">No extras available</p>
        </div>
      )}
    </div>
  );
};

export default ExtrasList;
